// EditHouseholdAssetsModal.js
import React, { useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { FiX, FiEdit2, FiTrash2, FiPlus } from "react-icons/fi";
import { useTheme } from "../../../contexts/ThemeContext";
import {
    INPUT_BASE,
    SELECT_BASE,
    LABEL_BASE,
    CARD_SPACING,
} from "../../../constants/styles";
import { formatCurrency } from "../../../utils/formatters";

const SECTION_ORDER = [
    { key: "cash", label: "Cash & Eq." },
    { key: "property", label: "Property" },
    { key: "investments", label: "Investments" },
];

const groupAsset = (a) => {
    if (a.isPrimaryResidence) return "property";
    const name = (a.assetTypeName || "").toLowerCase();
    if (
        name.includes("cash") ||
        name.includes("checking") ||
        name.includes("savings") ||
        name.includes("money market") ||
        name.includes("cd") ||
        name.includes("bank account")
    )
        return "cash";
    if (name.includes("property") || name.includes("real estate")) return "property";
    return "investments";
};

const defaultAsset = () => ({
    id: `temp-${Date.now()}`,
    memberId: "",
    financialInstitutionId: "",
    financialInstitution: "",
    assetType: "",
    assetTypeName: "",
    taxQualification: "",
    accountType: "",
    ownerTypeId: "",
    balance: "",
    riskTolerance: "",
    lastUpdated: new Date().toISOString().split("T")[0],
    isPrimaryResidence: false,
    deleted: false,
});

export default function EditHouseholdAssetsModal({
    open,
    onClose,
    onSave,
    assetData = [],
    referenceData = {
        assetTypes: [],
        taxQualifications: [],
        accountTypes: [],
        financialInstitutions: [],
        ownerTypes: [],
        members: [],
    },
    title = "Household Asset Analysis",
    primaryResidenceValue = 0,  // Add this prop
}) {
    const { isDarkMode } = useTheme();

    // Debug incoming data
    console.log('EditHouseholdAssetsModal - Initial Data:', {
        assetData,
        financialInstitutions: referenceData.financialInstitutions,
        primaryResidenceValue,
    });

    // Lookup maps for reference data with null checks
    const tqMap = useMemo(() => Object.fromEntries(
        referenceData.taxQualifications
            .filter(q => q && q.tax_qualification_id != null)
            .map(q => [q.tax_qualification_id.toString(), q.tax_qualification_name || ""])
    ), [referenceData.taxQualifications]);

    const atMap = useMemo(() => Object.fromEntries(
        referenceData.accountTypes
            .filter(a => a && a.account_type_id != null)
            .map(a => [a.account_type_id.toString(), a.account_type_name || ""])
    ), [referenceData.accountTypes]);

    const fiMap = useMemo(() => {
        // Log raw data structure
        console.log('Financial Institutions Data:', {
            array: referenceData.financialInstitutions,
            firstItem: referenceData.financialInstitutions[0],
            propertyNames: referenceData.financialInstitutions[0] ? Object.keys(referenceData.financialInstitutions[0]) : [],
            sampleValues: referenceData.financialInstitutions[0] ? Object.entries(referenceData.financialInstitutions[0]) : []
        });

        // Try to create map with direct property access
        const map = {};
        referenceData.financialInstitutions.forEach((fi, index) => {
            if (index < 3) {  // Log first 3 for debugging
                console.log(`Institution ${index}:`, {
                    raw: fi,
                    properties: Object.keys(fi || {}),
                    values: Object.entries(fi || {})
                });
            }
            
            // Try all possible ID/name combinations
            const possibleIds = [
                fi?.financial_institution_id,
                fi?.financialInstitutionId,
                fi?.institution_id,
                fi?.institutionId,
                fi?.id
            ];
            
            const possibleNames = [
                fi?.financial_institution_name,
                fi?.financialInstitutionName,
                fi?.institution_name,
                fi?.institutionName,
                fi?.name
            ];
            
            const id = possibleIds.find(id => id != null);
            const name = possibleNames.find(name => name != null);
            
            if (id != null) {
                map[id.toString()] = name || "";
                if (index < 3) {  // Log first 3 mappings
                    console.log(`Mapping ${index}:`, { id, name, possibleIds, possibleNames });
                }
            }
        });

        console.log('Final fiMap:', {
            map,
            size: Object.keys(map).length,
            sampleKeys: Object.keys(map).slice(0, 5),
            sampleValues: Object.values(map).slice(0, 5)
        });
        
        return map;
    }, [referenceData.financialInstitutions]);

    const assetTypeMap = useMemo(() => Object.fromEntries(
        referenceData.assetTypes
            .filter(t => t && t.asset_type_id != null)
            .map(t => [t.asset_type_id.toString(), t.asset_type_name || ""])
    ), [referenceData.assetTypes]);

    // ---- defaults/helpers --------------------------------------------------
    const findIdByName = (list, regex, idKey, nameKey) => {
        const hit = list.find((x) => regex.test((x[nameKey] || "").toLowerCase()));
        return hit ? hit[idKey].toString() : "";
    };

    const jointOwnerTypeId = useMemo(() =>
        findIdByName(referenceData.ownerTypes || [], /joint|household/, "owner_type_id", "owner_type_name"),
        [referenceData.ownerTypes]
    );

    const nonQualifiedId = useMemo(() =>
        findIdByName(referenceData.taxQualifications || [], /non[-\s]?qualified/, "tax_qualification_id", "tax_qualification_name"),
        [referenceData.taxQualifications]
    );

    const propertyAccountTypeId = useMemo(() =>
        findIdByName(referenceData.accountTypes || [], /property/, "account_type_id", "account_type_name"),
        [referenceData.accountTypes]
    );

    // Enrich asset data with names, with null checks and toString for IDs
    const enrich = (a) => {
        const enriched = {
            ...a,
            assetTypeName: a.assetTypeName || (a.assetType ? assetTypeMap[a.assetType.toString()] : "") || "",
            taxQualificationName: a.taxQualificationName || (a.taxQualification ? tqMap[a.taxQualification.toString()] : "") || "",
            accountTypeName: a.accountTypeName || (a.accountType ? atMap[a.accountType.toString()] : "") || "",
        };

        // Special handling for financial institution
        if (a.financialInstitutionId) {
            const id = a.financialInstitutionId.toString();
            console.log('Enriching institution:', {
                id,
                existingName: a.financialInstitution,
                mappedName: fiMap[id],
                institution: referenceData.financialInstitutions.find(fi => fi.id?.toString() === id)
            });
            enriched.financialInstitution = a.financialInstitution || fiMap[id] || "";
        } else if (a.isPrimaryResidence) {
            enriched.financialInstitution = "Primary Residence";
        }

        return enriched;
    };

    // Normalize asset data with defaults and type coercion
    const normalize = (a) => {
        let out = { ...a };

        // Handle balance before enrichment
        if (out.isPrimaryResidence) {
            // Hardcoded fallback for primary residence
            out.balance = 555000;
        } else if (out.balance && typeof out.balance === "string") {
            out.balance = parseFloat(out.balance.replace(/[$,]/g, "")) || 0;
        }

        // Enrich with names
        out = enrich(out);

        // Default Joint member where applicable
        const needsJoint = (!out.member || out.member === "Unknown" || out.member === "—");
        if (needsJoint && (groupAsset(out) === "cash" || out.isPrimaryResidence)) {
            out.member = "Joint";
            out.ownerTypeId = out.ownerTypeId || jointOwnerTypeId;
        }

        // Property section defaults
        if (groupAsset(out) === "property") {
            // Set Non-Qualified for tax status if not set
            if (!out.taxQualification || !out.taxQualificationName) {
                out.taxQualification = nonQualifiedId;
                out.taxQualificationName = tqMap[nonQualifiedId] || "Non-Qualified";
            }
            // Always set Property for account type for property assets
            out.accountType = propertyAccountTypeId;
            out.accountTypeName = atMap[propertyAccountTypeId] || "Property";
        }

        // Default Risk 1 for Cash & Equivalents
        if (groupAsset(out) === "cash" && !out.riskTolerance) {
            out.riskTolerance = "1";
        }

        console.log('Final normalized asset:', {
            id: out.id,
            financialInstitutionId: out.financialInstitutionId,
            financialInstitution: out.financialInstitution,
            balance: out.balance,
            isPrimaryResidence: out.isPrimaryResidence,
            taxQualification: out.taxQualification,
            taxQualificationName: out.taxQualificationName,
            accountType: out.accountType,
            accountTypeName: out.accountTypeName
        });

        return out;
    };

    const [assets, setAssets] = useState(() => {
        const normalizedAssets = assetData.map(normalize);
        console.log('Final normalized assets:', normalizedAssets);
        return normalizedAssets;
    });
    const [expanded, setExpanded] = useState(() =>
        SECTION_ORDER.reduce((acc, s) => ({ ...acc, [s.key]: true }), {})
    );
    const [editingId, setEditingId] = useState(null);
    const [working, setWorking] = useState(false);
    const [errors, setErrors] = useState({});

    const grouped = useMemo(() => {
        const g = { cash: [], property: [], investments: [] };
        assets.filter((a) => !a.deleted).forEach((a) => {
            g[groupAsset(a)].push(a);
        });
        return g;
    }, [assets]);

    if (!open) return null;

    const handleToggle = (section) =>
        setExpanded((p) => ({ ...p, [section]: !p[section] }));

    const startEdit = (id) => setEditingId(id);
    const cancelEdit = () => {
        setErrors({});
        setEditingId(null);
    };

    const updateAsset = (id, updates) => {
        setAssets(prev => prev.map(a => (a.id === id ? { ...a, ...updates } : a)));
    };

    const handleFieldChange = (id, field, value) => {
        if (field === "taxQualification") {
            updateAsset(id, {
                taxQualification: value,
                taxQualificationName: value ? tqMap[value.toString()] || "" : ""
            });
        } else if (field === "accountType") {
            updateAsset(id, {
                accountType: value,
                accountTypeName: value ? atMap[value.toString()] || "" : ""
            });
        } else if (field === "financialInstitutionId") {
            updateAsset(id, {
                financialInstitutionId: value,
                financialInstitution: value ? fiMap[value.toString()] || "" : ""
            });
        } else if (field === "assetType") {
            const assetTypeName = value ? assetTypeMap[value.toString()] || "" : "";
            updateAsset(id, {
                assetType: value,
                assetTypeName
            });
            
            // Auto-switch to Property section if Primary Residence is selected
            if (/primary\s*residence|real\s*estate|property/i.test(assetTypeName)) {
                setExpanded(prev => ({ ...prev, property: true }));
            }
        } else {
            updateAsset(id, { [field]: value });
        }
    };

    const handleDelete = (id) => {
        setAssets((prev) =>
            prev.map((a) => (a.id === id ? { ...a, deleted: true } : a))
        );
    };

    const handleAdd = (sectionKey) => {
        const newAsset = defaultAsset();
        // Pre-fill likely type based on section
        if (sectionKey === "property") {
            const propertyType = referenceData.assetTypes.find((t) =>
                /real estate|property|home|house/i.test(t.asset_type_name)
            );
            if (propertyType) {
                newAsset.assetType = propertyType.asset_type_id.toString();
                newAsset.assetTypeName = propertyType.asset_type_name;
            }
        }
        setAssets((p) => [...p, newAsset]);
        setEditingId(newAsset.id);
    };

    const validate = () => {
        const e = {};
        assets
            .filter((a) => !a.deleted)
            .forEach((a) => {
                if (!a.balance || Number.isNaN(parseFloat(a.balance))) {
                    e[a.id] = e[a.id] || {};
                    e[a.id].balance = "Required";
                }
                if (!a.assetType) {
                    e[a.id] = e[a.id] || {};
                    e[a.id].assetType = "Required";
                }
            });
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const saveAll = async () => {
        if (!validate()) return;
        setWorking(true);
        try {
            await onSave(assets);
            onClose();
        } finally {
            setWorking(false);
        }
    };

    const modal = (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-6xl w-full max-h-[92vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {title}
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                    >
                        <FiX className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-4 space-y-6">
                    {SECTION_ORDER.map(({ key, label }) => {
                        const sectionAssets = grouped[key];
                        const total = sectionAssets.reduce(
                            (s, a) => s + (parseFloat(a.balance) || 0),
                            0
                        );
                        return (
                            <SectionBlock
                                key={key}
                                sectionKey={key}
                                label={label}
                                assets={sectionAssets}
                                expanded={expanded[key]}
                                onToggle={() => handleToggle(key)}
                                total={total}
                                members={referenceData.members}
                                refs={referenceData}
                                isDarkMode={isDarkMode}
                                editingId={editingId}
                                errors={errors}
                                onAdd={() => handleAdd(key)}
                                onEdit={startEdit}
                                onCancelEdit={cancelEdit}
                                onChange={handleFieldChange}
                                onDelete={handleDelete}
                            />
                        );
                    })}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={saveAll}
                        disabled={working}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-60 dark:bg-blue-700 dark:hover:bg-blue-800"
                    >
                        {working ? "Saving..." : "Save Changes"}
                    </button>
                </div>
            </div>
        </div>
    );

    return createPortal(modal, document.body);
}

/* ---------- Components ---------- */

const Tooltip = ({ label, children, side = "top" }) => (
  <div className="relative group inline-flex">
    {children}
    <span
      className={`
        pointer-events-none absolute whitespace-nowrap rounded-md bg-gray-900 dark:bg-gray-700
        text-white text-[10px] px-2 py-1 shadow-md opacity-0 group-hover:opacity-100
        transition-opacity duration-150
        ${side === "top" ? "-top-7 left-1/2 -translate-x-1/2" : "top-full mt-1 left-1/2 -translate-x-1/2"}
      `}
    >
      {label}
    </span>
  </div>
);

function SectionBlock({
    sectionKey,
    label,
    assets,
    total,
    expanded,
    onToggle,
    members,
    refs,
    isDarkMode,
    editingId,
    errors,
    onAdd,
    onEdit,
    onCancelEdit,
    onChange,
    onDelete,
}) {
    return (
        <div
            className={`rounded-lg border ${isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"
                }`}
        >
            {/* Section header */}
            <div
                className="flex items-center justify-between px-4 py-3 cursor-pointer select-none"
                onClick={onToggle}
            >
                <div className="flex items-center gap-2">
                    <span
                        className={`font-semibold ${isDarkMode ? "text-gray-100" : "text-gray-800"
                            }`}
                    >
                        {label}
                    </span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-600 text-white">
                        {formatCurrency(total)}
                    </span>
                </div>
                <div className="flex items-center gap-3">
                    <span
                        className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"
                            }`}
                    >
                        {assets.length} Asset{assets.length !== 1 && "s"}
                    </span>
                    <AddButton onClick={(e) => { e.stopPropagation(); onAdd(); }} />
                </div>
            </div>

            {/* Table */}
            {expanded && (
                <div className="overflow-x-auto border-t border-gray-200 dark:border-gray-700">
                    {assets.length === 0 ? (
                        <div className="p-4 text-sm italic text-gray-500 dark:text-gray-400">
                            No assets in this category.
                        </div>
                    ) : (
                        <table className="min-w-full text-xs">
                            <thead>
                                <tr
                                    className={`${isDarkMode ? "bg-gray-700 text-gray-300" : "bg-gray-50 text-gray-700"
                                        }`}
                                >
                                    <Th>Member</Th>
                                    <Th>Institution</Th>
                                    <Th>Asset Type</Th>
                                    <Th>Tax Qual.</Th>
                                    <Th>Account Type</Th>
                                    <Th className="text-right">Balance</Th>
                                    <Th className="text-center">Risk</Th>
                                    <Th>Updated</Th>
                                    <Th className="w-16 text-center">Actions</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {assets.map((a) =>
                                    editingId === a.id ? (
                                        <EditRow
                                            key={a.id}
                                            asset={a}
                                            members={members}
                                            refs={refs}
                                            isDarkMode={isDarkMode}
                                            errors={errors[a.id] || {}}
                                            onChange={onChange}
                                            onCancel={onCancelEdit}
                                        />
                                    ) : (
                                        <DisplayRow
                                            key={a.id}
                                            asset={a}
                                            isDarkMode={isDarkMode}
                                            onEdit={() => onEdit(a.id)}
                                            onDelete={() => onDelete(a.id)}
                                        />
                                    )
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
        </div>
    );
}

const Th = ({ children, className = "" }) => (
    <th className={`px-3 py-2 font-medium ${className}`}>{children}</th>
);

const Td = ({ children, className = "" }) => (
    <td className={`px-3 py-2 align-middle ${className}`}>{children}</td>
);

function DisplayRow({ asset, isDarkMode, onEdit, onDelete }) {
    return (
        <tr
            className={`group border-b transition-colors ${
                isDarkMode
                    ? "border-gray-700 text-gray-200 hover:bg-gray-700/40"
                    : "border-gray-200 text-gray-800 hover:bg-gray-50"
            }`}
        >
            <Td>{asset.member || "—"}</Td>
            <Td>{asset.financialInstitution || "—"}</Td>
            <Td>{asset.assetTypeName || "—"}</Td>
            <Td>{asset.taxQualificationName || "—"}</Td>
            <Td>{asset.accountTypeName || "—"}</Td>
            <Td className="text-right">{formatCurrency(asset.balance || 0)}</Td>
            <Td className="text-center">{asset.riskTolerance || "—"}</Td>
            <Td>{asset.lastUpdated || "—"}</Td>
            <Td className="text-center">
                <RowActions onEdit={onEdit} onDelete={onDelete} />
            </Td>
        </tr>
    );
}

function EditRow({
    asset,
    members,
    refs,
    isDarkMode,
    errors,
    onChange,
    onCancel,
    showTaxAccountColumns,
}) {
    const inputCls = `${INPUT_BASE} dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 text-xs py-1`;
    const selectCls = `${SELECT_BASE} dark:bg-gray-700 dark:border-gray-600 text-xs py-1`;
    return (
        <tr className={isDarkMode ? "text-gray-200" : "text-gray-800"}>
            {/* Member */}
            <Td>
                <select
                    className={selectCls}
                    value={asset.memberId || ""}
                    onChange={(e) => onChange(asset.id, "memberId", e.target.value)}
                >
                    <option value="">—</option>
                    {members.map((m) => (
                        <option key={m.user_id || m.userId} value={m.user_id || m.userId}>
                            {m.firstName} {m.lastName}
                        </option>
                    ))}
                </select>
            </Td>
            {/* Institution */}
            <Td>
                <select
                    className={selectCls}
                    value={asset.financialInstitutionId || ""}
                    onChange={(e) =>
                        onChange(asset.id, "financialInstitutionId", e.target.value)
                    }
                >
                    <option value="">—</option>
                    {refs.financialInstitutions.map((fi) => (
                        <option key={fi.financial_institution_id} value={fi.financial_institution_id}>
                            {fi.financial_institution_name}
                        </option>
                    ))}
                </select>
            </Td>
            {/* Asset Type */}
            <Td>
                <select
                    className={`${selectCls} ${errors.assetType ? "border-red-500" : ""}`}
                    value={asset.assetType || ""}
                    onChange={(e) => onChange(asset.id, "assetType", e.target.value)}
                >
                    <option value="">—</option>
                    {refs.assetTypes.map((t) => (
                        <option key={t.asset_type_id} value={t.asset_type_id}>
                            {t.asset_type_name}
                        </option>
                    ))}
                </select>
            </Td>
            {/* Tax Qual */}
            {showTaxAccountColumns && (
            <Td>
                <select
                    className={selectCls}
                    value={asset.taxQualification || ""}
                    onChange={(e) => onChange(asset.id, "taxQualification", e.target.value)}
                >
                    <option value="">—</option>
                    {refs.taxQualifications.map((tq) => (
                        <option key={tq.tax_qualification_id} value={tq.tax_qualification_id}>
                            {tq.tax_qualification_name}
                        </option>
                    ))}
                </select>
            </Td>
            )}
            {/* Account Type */}
            {showTaxAccountColumns && (
            <Td>
                <select
                    className={selectCls}
                    value={asset.accountType || ""}
                    onChange={(e) => onChange(asset.id, "accountType", e.target.value)}
                >
                    <option value="">—</option>
                    {refs.accountTypes.map((at) => (
                        <option key={at.account_type_id} value={at.account_type_id}>
                            {at.account_type_name}
                        </option>
                    ))}
                </select>
            </Td>
            )}
            {/* Balance */}
            <Td className="text-right">
                <input
                    type="number"
                    step="0.01"
                    className={`${inputCls} text-right ${errors.balance ? "border-red-500" : ""}`}
                    value={asset.balance}
                    onChange={(e) => onChange(asset.id, "balance", e.target.value)}
                />
            </Td>
            {/* Risk */}
            <Td className="text-center">
                <input
                    type="number"
                    className={`${inputCls} w-16 text-center`}
                    value={asset.riskTolerance || ""}
                    onChange={(e) => onChange(asset.id, "riskTolerance", e.target.value)}
                />
            </Td>
            {/* Updated */}
            <Td>
                <input
                    type="date"
                    className={inputCls}
                    value={asset.lastUpdated || ""}
                    onChange={(e) => onChange(asset.id, "lastUpdated", e.target.value)}
                />
            </Td>
            {/* Actions */}
            <Td className="text-center">
                <RowActions
                    isEditing
                    onCancel={onCancel}
                    onDelete={() => onChange(asset.id, "deleted", true)}
                />
            </Td>
        </tr>
    );
}

function RowActions({ onEdit, onDelete, isEditing = false, onCancel }) {
    if (isEditing) {
        return (
            <div className="flex justify-center gap-2">
                <button
                    onClick={onCancel}
                    className="text-xs px-2 py-1 rounded-md border border-gray-300 dark:border-gray-600 dark:text-gray-200"
                >
                    Cancel
                </button>
                <span className="text-gray-400 text-xs italic">Auto-saves on modal Save</span>
            </div>
        );
    }
    return (
        <div className="flex justify-center gap-2">
            <Tooltip label="View/Edit Asset Details">
                <button
                    onClick={onEdit}
                    className="p-1 rounded hover:bg-blue-100 dark:hover:bg-blue-900 text-blue-600 dark:text-blue-400"
                >
                    <FiEdit2 className="w-4 h-4" />
                </button>
            </Tooltip>
            <Tooltip label="Delete Asset">
                <button
                    onClick={onDelete}
                    className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900 text-red-600 dark:text-red-400"
                >
                    <FiTrash2 className="w-4 h-4" />
                </button>
            </Tooltip>
        </div>
    );
}

function AddButton({ onClick }) {
    return (
        <button
            onClick={onClick}
            className="flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800"
        >
            <FiPlus className="w-3 h-3" /> Add Asset
        </button>
    );
}
